import {restRequest} from '@nlabs/metropolisjs';

import type {FluxFramework} from '@nlabs/arkhamjs';
import type {CreditBalance, FreeMembership, PlanId} from '@reactorbox/shared';
import type {AuthUser} from './authActions.js';

export interface AccountDetails {
  readonly credits: CreditBalance | null;
  readonly membership: FreeMembership;
  readonly hasSubscription?: boolean;
  readonly plan?: PlanId;
  readonly upgradeStatus: 'available' | 'unconfigured';
  readonly user: AuthUser;
}

export const ACCOUNT_EVENTS = {
  CHANGED: 'REACTORBOX_ACCOUNT_CHANGED',
  ERROR: 'REACTORBOX_ACCOUNT_ERROR',
  PENDING: 'REACTORBOX_ACCOUNT_PENDING'
} as const;

export const createAccountActions = (flux: FluxFramework) => {
  const epoch = flux.getState('auth.epoch', 0);
  const userId = (flux.getState('auth.user') as AuthUser | undefined)?.id;
  let pending = false;
  const current = (): boolean => flux.getState('auth.epoch', 0) === epoch
    && (flux.getState('auth.user') as AuthUser | undefined)?.id === userId;
  return {
    load: async (): Promise<void> => {
      if(pending || !userId || !current()) {
        return;
      }
      pending = true;
      await flux.dispatch({type: ACCOUNT_EVENTS.PENDING});
      try {
        const response = await restRequest<AccountDetails | {error: string}>(flux, '/api/account', 'GET', undefined, {queueOffline: false});
        if(!current()) {
          return;
        }
        if(response && 'error' in response) {
          throw new Error(response.error);
        }
        if(!response || response.user?.id !== userId || !['available', 'unconfigured'].includes(response.upgradeStatus)
          || !response.membership || !['first_month', 'upgrade_available'].includes(response.membership.phase)
          || !Number.isSafeInteger(response.membership.daysRemaining) || response.membership.daysRemaining < 0
          || !Number.isFinite(Date.parse(response.membership.startedAt))
          || !Number.isFinite(Date.parse(response.membership.upgradeAvailableAt))
          || Date.parse(response.membership.upgradeAvailableAt) <= Date.parse(response.membership.startedAt)
          || (response.credits !== null && !['available', 'reserved', 'spent'].every((key) => {
            const value = response.credits?.[key as keyof CreditBalance];
            return typeof value === 'number' && Number.isSafeInteger(value) && value >= 0;
          }))) {
          throw new Error('Your account details could not be confirmed. Please refresh.');
        }
        await flux.setState('account.data', response);
        await flux.setState('account.error', '');
        await flux.dispatch({type: ACCOUNT_EVENTS.CHANGED});
      } catch(error) {
        if(current()) {
          await flux.setState('account.data', undefined);
          await flux.setState('account.error', error instanceof Error ? error.message : 'Unable to load your account.');
          await flux.dispatch({type: ACCOUNT_EVENTS.ERROR});
        }
      } finally {
        pending = false;
      }
    },
    openBilling: async (plan?: string): Promise<void> => {
      if(pending || !userId || !current()) {
        return;
      }
      pending = true;
      await flux.dispatch({type: ACCOUNT_EVENTS.PENDING});
      try {
        const response = await restRequest<{url: string} | {error: string}>(flux, plan ? '/api/billing/checkout' : '/api/billing/portal', 'POST', plan ? {plan} : {}, {queueOffline: false});
        if(!current()) {
          return;
        }
        if(!response || 'error' in response) {
          throw new Error(response && 'error' in response ? response.error : 'Billing could not be opened.');
        }
        const url = new URL(response.url);
        if(url.protocol !== 'https:' || !['checkout.stripe.com', 'billing.stripe.com'].includes(url.hostname)) {
          throw new Error('Unexpected billing destination.');
        }
        window.location.assign(url.href);
      } catch(error) {
        if(current()) {
          await flux.setState('account.error', error instanceof Error ? error.message : 'Unable to open billing.');
          await flux.dispatch({type: ACCOUNT_EVENTS.ERROR});
        }
      } finally {
        pending = false;
      }
    }
  };
};
